import React from 'react';
import {
    AgeDisplayMode,
    AgeEstimationModelField,
    AntispoofingModelField,
    Endpoint,
    MetadataField,
    OperatorField,
    Sections,
    SecureMode,
    MultiframeMode,
    validRoleConfig,
    ImageValidationLevelField,
} from '@models/prediction';
import { Config as ConsentConfig } from '@components/consent';
import { ConfigSubpanelProps } from './ConfigPanel';
import { ConfigWrapper } from './ConfigWrapper';
import EndpointsSection from './endpoints';
import LevelOfAssuranceFieldSection from './levelOfAssurance';
import MetadataFieldSection from './metadataField';

import { useGlobalContext } from '@/globalContext';
import Config from '@utils/config';
import AgeDisplayModeSection from './ageDisplayMode/AgeDisplayModeSection';
import AgeEstimationModelSection from './ageEstimationModelField/AgeEstimationModelSection';
import AntispoofingModelSection from './antispoofingModelField/AntispoofingModelSection';
import LanguageFieldSection from './languageField/LanguageFieldSection';
import OperatorFieldSection from './operator/OperatorFieldSection';
import SecureModeSection from './secure/SecureModeSection';
import ThresholdFieldSection from './threshold/ThresholdFieldSection';
import MultiframeModeSection from './multiframe/MultiframeModeSection';
import { RoleCode } from '@models/account';
import ImageValidationLevelSection from './imageValidationLevel';

// TODO: AITOOL-2804 - Replace this for Object.values(AgeAntispoofingEndpoints) after the refactor.
const endpointsList = [
    Endpoint.Antispoofing,
    Endpoint.Age,
    Endpoint.AgeAntispoofing,
    Endpoint.AgeAntispoofingVerify,
    Endpoint.AgeVerify,
    Endpoint.SelfCheckoutAntiSpoofing,
];
const metadataFieldsList = Object.values(MetadataField);
const ageEstimationModelFieldsList = Object.values(AgeEstimationModelField);
const levelOfAssuranceFieldList = Config.levelAssuranceValues;
const operatorFieldList = Object.values(OperatorField);
const secureModeList = Object.values(SecureMode);
const multiframeModeList = Object.values(MultiframeMode);
const ageDisplayModeList = Object.values(AgeDisplayMode);
const imageValidationLevelList = Object.values(ImageValidationLevelField);
const antispoofingModelFieldsList = Object.values(AntispoofingModelField);

const getEndpointListByRole = (role: RoleCode | undefined): Endpoint[] => {
    switch (role) {
        case RoleCode.Admin:
            return endpointsList.filter((endpoint) =>
                Config.allowedEndpointsForAdmin.includes(endpoint),
            );
        case RoleCode.User:
            return endpointsList.filter((endpoint) =>
                Config.allowedEndpointsForUser.includes(endpoint),
            );
        case RoleCode.Tester:
            return endpointsList.filter((endpoint) =>
                Config.allowedEndpointsForTester.includes(endpoint),
            );
        case RoleCode.DataCollector:
            return endpointsList.filter((endpoint) =>
                Config.allowedEndpointsForDataCollector.includes(endpoint),
            );
        default:
            return [];
    }
};

export const AgeAntispoofingConfigPanel: React.FC<ConfigSubpanelProps> = ({
    config: initialConfig,
    onConfigChange,
    onError,
}) => {
    const { account } = useGlobalContext();
    const currentRole = account?.role;

    const createHandler = <K extends keyof typeof initialConfig>(key: K) => (
        value: typeof initialConfig[K],
    ) => onConfigChange({ ...initialConfig, [key]: value });

    const createToggleHandler = <K extends keyof typeof initialConfig>(key: K) => (
        value: typeof initialConfig[K],
    ) =>
        onConfigChange({
            ...initialConfig,
            [key]: value === initialConfig[key] ? undefined : value,
        });

    const onLevelOfAssuranceChange = createToggleHandler('selectedLevelOfAssuranceField');
    const onAgeEstimationModelChange = createToggleHandler('selectedAgeEstimationModelField');
    const onMetadataChange = createToggleHandler('selectedMetadataField');
    const onImageValidationLevelChange = createToggleHandler('selectedImageValidationLevelField');
    const onAntispoofingModelChange = createToggleHandler('selectedAntispoofingModelField');
    const onOperatorChange = createToggleHandler('selectedOperatorField');
    const onThresholdChange = createToggleHandler('selectedThresholdField');
    const onSecureModeChange = createHandler('selectedSecureMode');
    const onMultiframeModeChange = createHandler('selectedMultiframeMode');
    const onAgeDisplayModeChange = createHandler('selectedAgeDisplayMode');
    const onLanguageFieldChange = createHandler('selectedLanguage');
    const onConsentChange = createHandler('consent');

    const isSelfCheckoutAntispoofing =
        initialConfig.selectedEndpoint === Endpoint.SelfCheckoutAntiSpoofing;
    const isAntispoofing = initialConfig.selectedEndpoint === Endpoint.Antispoofing;
    const isAge = initialConfig.selectedEndpoint === Endpoint.Age;
    const isAgeVerify = initialConfig.selectedEndpoint === Endpoint.AgeVerify;
    const isAgeAntispoofingVerify =
        initialConfig.selectedEndpoint === Endpoint.AgeAntispoofingVerify;
    const isFaceMatching = initialConfig.selectedEndpoint === Endpoint.FaceMatching;
    const isAgeAntispoofing = initialConfig.selectedEndpoint === Endpoint.AgeAntispoofing;
    const isSecureModeSecure = initialConfig.selectedSecureMode === SecureMode.Secure;

    return (
        <>
            {validRoleConfig(Sections.Endpoint, currentRole) && (
                <EndpointsSection
                    endpointsList={getEndpointListByRole(currentRole)}
                    config={initialConfig}
                    onConfigChange={onConfigChange}
                />
            )}
            {validRoleConfig(Sections.Metadata, currentRole) && !isSelfCheckoutAntispoofing && (
                <MetadataFieldSection
                    metadataFieldList={metadataFieldsList}
                    value={initialConfig.selectedMetadataField}
                    onChange={onMetadataChange}
                />
            )}
            {validRoleConfig(Sections.AgeEstimationModel, currentRole) &&
                !(isAntispoofing || isSelfCheckoutAntispoofing || isFaceMatching) && (
                    <AgeEstimationModelSection
                        list={ageEstimationModelFieldsList}
                        value={initialConfig.selectedAgeEstimationModelField}
                        onChange={onAgeEstimationModelChange}
                    />
                )}
            {validRoleConfig(Sections.AntispoofingModel, currentRole) &&
                (isAntispoofing || isAgeAntispoofing || isAgeAntispoofingVerify) && (
                    <AntispoofingModelSection
                        list={antispoofingModelFieldsList}
                        value={initialConfig.selectedAntispoofingModelField}
                        onChange={onAntispoofingModelChange}
                    />
                )}
            {validRoleConfig(Sections.LevelOfAssurance, currentRole) &&
                !(isAge || isAgeVerify || isSelfCheckoutAntispoofing) && (
                    <LevelOfAssuranceFieldSection
                        levelOfAssuranceFieldList={levelOfAssuranceFieldList}
                        value={initialConfig.selectedLevelOfAssuranceField}
                        onChange={onLevelOfAssuranceChange}
                    />
                )}
            {validRoleConfig(Sections.Operator, currentRole) &&
                (isAgeVerify || isAgeAntispoofingVerify) && (
                    <OperatorFieldSection
                        operatorFieldList={operatorFieldList}
                        value={initialConfig.selectedOperatorField}
                        onChange={onOperatorChange}
                    />
                )}
            {validRoleConfig(Sections.Threshold, currentRole) &&
                (isAgeVerify || isAgeAntispoofingVerify) && (
                    <ThresholdFieldSection
                        value={initialConfig.selectedThresholdField}
                        onChange={onThresholdChange}
                    />
                )}
            {validRoleConfig(Sections.SecureMode, currentRole) && (
                <SecureModeSection
                    secureModeList={secureModeList}
                    value={initialConfig.selectedSecureMode}
                    onChange={onSecureModeChange}
                />
            )}
            {validRoleConfig(Sections.MultiframeMode, currentRole) && isSecureModeSecure && (
                <MultiframeModeSection
                    multiframeModeList={multiframeModeList}
                    value={initialConfig.selectedMultiframeMode}
                    onChange={onMultiframeModeChange}
                />
            )}
            {validRoleConfig(Sections.AgeDisplayMode, currentRole) &&
                (isAge || isAgeAntispoofing) && (
                    <AgeDisplayModeSection
                        ageDisplayModeList={ageDisplayModeList}
                        value={initialConfig.selectedAgeDisplayMode}
                        onChange={onAgeDisplayModeChange}
                    />
                )}
            {validRoleConfig(Sections.ImageValidationLevel, currentRole) &&
                (isAge || isAgeVerify) && (
                    <ImageValidationLevelSection
                        imageValidationLevelList={imageValidationLevelList}
                        value={initialConfig.selectedImageValidationLevelField}
                        onChange={onImageValidationLevelChange}
                    />
                )}
            {validRoleConfig(Sections.Language, currentRole) && (
                <LanguageFieldSection
                    value={initialConfig.selectedLanguage}
                    onChange={onLanguageFieldChange}
                />
            )}
            {validRoleConfig(Sections.ConfigWrapper, currentRole) && (
                <ConfigWrapper onError={onError} property={'saveImagesScanTool'} />
            )}
            {Config.showConsent && (
                <ConsentConfig onChange={onConsentChange} consentGiven={initialConfig.consent} />
            )}
        </>
    );
};
